import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { useFeed } from '@/lib/use-feed';
import { ArticleFeed } from '@/components/content';
import { Icon } from '@/components/shell';
import { PageHeader, Section } from './page-parts';
import NotFound from './NotFound';

/**
 * PRD §14's local coverage: `/local` lists the districts, `/local/:districtSlug` is one
 * district's feed. `local` is a reserved slug precisely so these routes can exist
 * alongside category URLs.
 */
export default function DistrictPage() {
  const { locale } = useLocale();
  const { districtSlug } = useParams();

  const { data: districts } = useQuery({
    queryKey: queryKeys.districts(locale),
    queryFn: () => publicApi.districts(locale),
    staleTime: STALE.taxonomy,
  });

  if (!districtSlug) return <LocalHub />;

  const district = districts?.find((row) => row.slug === districtSlug);
  if (districts && !district) return <NotFound />;

  return (
    <DistrictFeed slug={districtSlug} name={district?.name} description={district?.description} />
  );
}

function LocalHub() {
  const { locale, t } = useLocale();
  const { data: districts, isPending } = useQuery({
    queryKey: queryKeys.districts(locale),
    queryFn: () => publicApi.districts(locale),
    staleTime: STALE.taxonomy,
  });

  return (
    <>
      <PageHeader title={t('nav.local')} subtitle={t('footer.localNews')} />
      <Section>
        {isPending ? (
          <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4">
            {Array.from({ length: 7 }, (_, index) => (
              <div key={index} className="nk-skeleton h-24 rounded-lg" aria-hidden="true" />
            ))}
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4">
            {(districts ?? []).map((district) => (
              <li key={district.id}>
                <Link
                  to={district.path}
                  className="flex h-full flex-col gap-1.5 rounded-lg border border-line bg-white p-4 transition hover:border-red"
                >
                  <span className="flex items-center gap-2 text-lg font-bold text-ink">
                    <Icon name="local" size={16} className="text-red" />
                    {district.name}
                  </span>
                  {district.description && (
                    <span className="text-xs text-muted">{district.description}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

function DistrictFeed({
  slug,
  name,
  description,
}: {
  slug: string;
  name?: string;
  description?: string | null;
}) {
  const { locale, t } = useLocale();
  const feed = useFeed(locale, { district: slug, limit: 12 });

  return (
    <>
      <PageHeader title={name ?? slug.replace(/-/g, ' ')} subtitle={description ?? undefined} />
      <Section>
        <ArticleFeed
          articles={feed.articles}
          isPending={feed.isPending}
          isError={feed.isError}
          hasNextPage={Boolean(feed.hasNextPage)}
          isFetchingNextPage={feed.isFetchingNextPage}
          onLoadMore={() => void feed.fetchNextPage()}
          onRetry={() => void feed.refetch()}
          emptyTitle={t('empty.noStories')}
        />
      </Section>
    </>
  );
}
